import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/scheduleTour';

// ─── Slot group definitions ───────────────────────────────────────────────────

/** Single static background — not a photo_gallery slideshow. */
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
    },
  },
  // Manual swap keeps alt matched to the chosen gallery photo.
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo of a standout amenity — pool, fitness center, clubhouse, lounge, courtyard, rooftop, grilling area, or similar. Do not reuse the property featured image or hero exterior shot. Avoid less desirable amenities such as laundry rooms, parking lots, package lockers, mailrooms, trash areas, and storage.',
    context:
      'Full-bleed background for the Pinned Callouts band on the home page, below the hero. Must differ from the hero/featured property image.',
  },
};

const cta1Def: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta2Def: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const bg = useSlotGroup('pinnedcallouts.bg_image', bgImageDef);
  const cta1 = useSlotGroup('pinnedcallouts.cta1', cta1Def);
  const cta2 = useSlotGroup('pinnedcallouts.cta2', cta2Def);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden min-h-[340px] md:min-h-[440px] flex items-center justify-center">
      <div className="absolute inset-0 z-0">
        <EditableSlotGroup groupPath="pinnedcallouts.bg_image" definition={bgImageDef}>
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              className="absolute inset-0 w-full h-full object-cover object-center"
              sizes="100vw"
              priority
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
          )}
        </EditableSlotGroup>
      </div>

      {/* Primary overlay (z-[1], above bg, below content) */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            'color-mix(in srgb, var(--vibe-color-primary) 55%, transparent)',
        }}
      />

      <div
        ref={contentRef}
        className="reveal-zoom-front relative z-10 text-center max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
      >
        <RichTextContent
          path="pinnedcallouts.subtitle"
          label="Subtitle"
          defaultValue="<p>Experience The Difference</p>"
          as="p"
          className="font-body text-sm sm:text-base font-semibold uppercase tracking-[0.3em] text-white/80 mb-5"
        />

        <div className="w-10 h-px bg-white/60 mx-auto mb-6" />

        <RichTextContent
          path="pinnedcallouts.title"
          label="Title"
          defaultValue="<p>Live Where You Love</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.05] mb-10"
        />

        <div className="flex flex-col sm:inline-grid sm:grid-cols-2 gap-4 items-center sm:items-stretch mx-auto">
          <EditableSlotGroup
            groupPath="pinnedcallouts.cta1"
            definition={cta1Def}
            className="w-full max-md:w-56"
          >
            <a
              href={scheduleTourHref(cta1.url || '/schedule-a-tour')}
              data-schedule-tour-href={cta1.url || '/schedule-a-tour'}
              target={cta1._target || '_self'}
              onClick={handleScheduleTourClick}
              className="pinnedcallouts-cta group/cta relative inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta1.label || 'Schedule a Tour'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="pinnedcallouts.cta2"
            definition={cta2Def}
            className="w-full max-md:w-56"
          >
            <a
              href={cta2.url || '/contact-us'}
              target={cta2._target || '_self'}
              className="pinnedcallouts-cta group/cta relative inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta2.label || 'Contact Us'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pinnedcallouts-cta {
          background: #ffffff;
          border: 0;
          border-radius: 5px;
          color: var(--vibe-color-primary);
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .pinnedcallouts-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .pinnedcallouts-cta:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .pinnedcallouts-cta:hover::before {
          opacity: 0;
        }
        .pinnedcallouts-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experience The Difference</p>',
    },
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Live Where You Love</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.bg_image': bgImageDef,
    'pinnedcallouts.cta1': cta1Def,
    'pinnedcallouts.cta2': cta2Def,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
